import axios from 'axios'

import { debugLog } from './debugLog'

// ─── Configurable API base (CORS-safe local dev) ──────────────────────────────
// The app hardcodes `https://api.irhabd.com` in ~126 call sites. In production that is
// correct (the UI is served from an allowed origin). In dev the browser runs on
// http://localhost:<port>, which the production API's locked-down CORS rejects. Rather
// than edit every call site, we rewrite the base here: set REACT_APP_API_URL=/api in
// .env.development so requests go through the CRA dev proxy (src/setupProxy.js) as
// same-origin, avoiding CORS. Defaults to the prod host, so the production build is
// unchanged (and the rewrite interceptor is not even registered).
export const API_BASE = process.env.REACT_APP_API_URL || 'https://api.irhabd.com'
const HARDCODED_API = 'https://api.irhabd.com'

if (API_BASE !== HARDCODED_API) {
  axios.interceptors.request.use((config) => {
    if (typeof config.url === 'string' && config.url.startsWith(HARDCODED_API)) {
      config.url = API_BASE + config.url.slice(HARDCODED_API.length)
    }
    return config
  })
}

// The login endpoint answers 401 for bad credentials. That belongs to Login's own catch,
// and must not wipe the token of a signed-in user who happens to retry credentials.
const LOGIN_URL_FRAGMENT = '/v1/login'

// Drop every trace of the session. AuthWrapper (src/App.js) only tests truthiness, but
// removing the key keeps the call sites that build `Bearer ${localStorage.getItem('token')}`
// themselves from sending an empty bearer.
export const clearAuth = () => {
  localStorage.removeItem('token')
  delete axios.defaults.headers.common['Authorization']
}

// A token can stop working without expiring - revoked server side, user disabled, secret
// rotated. The exp check in AuthWrapper can't see that, so the server's 401 is the signal.
// Every caller in the app uses the default axios instance (no axios.create anywhere), so
// this single interceptor covers them all.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status
    const url = error?.config?.url || ''

    // 401 only. 403 means authenticated but not permitted, and pages such as
    // TicketDetail render their own message for it.
    if (status === 401 && !url.includes(LOGIN_URL_FRAGMENT)) {
      clearAuth()
      debugLog('Token rejected by server')
      // Runs outside the React tree, so navigate the way HashRouter reads. The guard keeps
      // concurrent 401s idempotent and stops HotAxleAlert - which mounts outside
      // AuthWrapper and polls every minute - from bouncing the login page.
      if (!window.location.hash.startsWith('#/login')) {
        window.location.hash = '#/login'
      }
    }

    return Promise.reject(error)
  },
)
